import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { AlertCircle, Mail01 } from '@untitledui/icons';
import { FeaturedIcon } from '@/components/_untitled/foundations/featured-icon/featured-icon';
import type { KycStepProps } from '../../config/kycSteps.config';
import { PinInput } from "@/components/_untitled/base/pin-input/pin-input.tsx";
import { Button } from '@/components/_untitled/base/buttons/button';
import { authApi } from '@/api/authApi';
import { StepFooter } from '../StepFooter';
import { prefixes, useTypedTranslation } from "@/locales";

export function VerifyEmailStep({ data, onComplete, onBack }: KycStepProps) {
    const [tVerifyEmail, tErrors] = useTypedTranslation(
        prefixes('kyc.steps.verifyEmail', 'kyc.steps.verifyEmail.errors')
    );
    const [searchParams, setSearchParams] = useSearchParams();
    const [code, setCode] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasAttemptedVerification, setHasAttemptedVerification] = useState(false);

    // Pre-fill code from URL if present (from email link)
    useEffect(() => {
        const codeFromUrl = searchParams.get('code');
        if (codeFromUrl && codeFromUrl.length === 4) {
            setCode(codeFromUrl);
            // Clean up URL param
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('code');
            setSearchParams(newParams, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const handleCodeChange = useCallback((value: string) => {
        setCode(value);
        setError(null);
        setHasAttemptedVerification(false);
    }, []);

    const handleVerify = useCallback(async () => {
        if (code.length !== 4 || !data.email) return;

        setIsVerifying(true);
        setError(null);
        setHasAttemptedVerification(true);

        try {
            await authApi.verifyEmail(data.email, code);
            onComplete({ emailVerificationStatus: 'VERIFIED' });
        } catch (err) {
            if (err instanceof Error && 'response' in err) {
                const response = (err as {
                    response?: {
                        data?: {
                            message?: string;
                            errorCode?: string;
                            remainingAttempts?: number;
                            remainingMinutes?: number
                        }
                    }
                }).response;
                const errorData = response?.data;

                if (errorData?.errorCode) {
                    switch (errorData.errorCode) {
                        case 'INVALID_CODE_WITH_ATTEMPTS':
                            setError(tErrors('invalidCodeWithAttempts', {
                                count: errorData.remainingAttempts || 0
                            }));
                            break;
                        case 'ACCOUNT_LOCKED':
                        case 'ACCOUNT_LOCKED_MAX_ATTEMPTS':
                            setError(tErrors('accountLocked', {
                                minutes: errorData.remainingMinutes || 60
                            }));
                            break;
                        case 'VERIFICATION_CODE_EXPIRED':
                            setError(tErrors('codeExpired'));
                            break;
                        case 'NO_VERIFICATION_CODE':
                            setError(tErrors('noCode'));
                            break;
                        default:
                            setError(tErrors('invalidCode'));
                    }
                } else {
                    setError(errorData?.message || tErrors('invalidCode'));
                }
            } else {
                setError(tErrors('invalidCode'));
            }
        } finally {
            setIsVerifying(false);
        }
    }, [code, data.email, onComplete, tErrors]);

    const handleResend = useCallback(async () => {
        if (!data.email) return;

        setIsResending(true);
        setError(null);

        try {
            await authApi.resendVerification(data.email);
            setCode('');
            setHasAttemptedVerification(false);
        } catch (err) {
            const errorMessage = err instanceof Error && 'response' in err
                ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
                : undefined;
            setError(errorMessage || tErrors('genericError'));
        } finally {
            setIsResending(false);
        }
    }, [data.email, tErrors]);

    const handleSkip = () => {
        onComplete({ emailVerificationStatus: 'SKIPPED' });
    };

    // Auto-verify when 4 digits are entered (only once per code)
    useEffect(() => {
        if (code.length === 4 && !isVerifying && !hasAttemptedVerification) {
            void handleVerify();
        }
    }, [code, handleVerify, isVerifying, hasAttemptedVerification]);

    return (
        <div className="flex flex-col items-center gap-6 mb-6">
            <FeaturedIcon icon={ Mail01 } color="brand" theme="light" size="xl"/>

            <div className="flex flex-col items-center gap-0 w-full">
                <h2 className="text-display-sm font-normal text-primary text-center">
                    { tVerifyEmail('title') }
                </h2>
                <p className="text-md text-tertiary text-center whitespace-pre-line">
                    { tVerifyEmail('description') }
                </p>
            </div>

            <div className="flex flex-col items-center justify-center gap-2 py-5 w-full">
                <PinInput disabled={ isVerifying } size="md">
                    <PinInput.Label className="sr-only">{ tVerifyEmail('codeLabel') }</PinInput.Label>
                    <PinInput.Group maxLength={ 4 } value={ code } onChange={ handleCodeChange }
                                    containerClassName="rtl:flex-row-reverse">
                        <PinInput.Slot index={ 0 }/>
                        <PinInput.Slot index={ 1 }/>
                        <PinInput.Slot index={ 2 }/>
                        <PinInput.Slot index={ 3 }/>
                    </PinInput.Group>
                </PinInput>
            </div>

            { error && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-error-50 border border-error-200 w-full">
                    <AlertCircle className="size-5 text-error-600 shrink-0 mt-0.5"/>
                    <p className="text-sm text-error-600">{ error }</p>
                </div>
            ) }

            <Button
                color="link-gray"
                onClick={ handleResend }
                disabled={ isResending || isVerifying }
                className="text-sm"
            >
                { tVerifyEmail(isResending ? 'resending' : 'resend') }
            </Button>

            <StepFooter
                primary={ {
                    label: tVerifyEmail('skip'),
                    onClick: handleSkip,
                    loading: isVerifying
                } }
                back={ { show: true, onClick: onBack } }
            />
        </div>
    );
}
